// apk.ts — Capture APK install/update over WebADB (EGO-96).
//
// The release manifest + APKs live in R2 behind the Worker's JWT-gated
// /releases endpoints (published by tools/release-apk.sh). This module:
//   1. reads the latest release + the device's installed versionCode
//   2. downloads the APK through the Worker, verifies its sha256 in-browser
//   3. pushes it to /data/local/tmp and runs `pm install -r`
// No adb "install" verb exists in the sync protocol — push + pm is exactly
// what native `adb install` does under the hood.
import type { Adb } from "@yume-chan/adb";
import { WrapConsumableStream } from "@yume-chan/stream-extra";
import { supabase } from "./auth.ts";

// Build-time override (EGO-112): dev/staging builds point at the staging
// worker via esbuild --define:__WORKER_URL__; unset = production.
declare const __WORKER_URL__: string;
export const WORKER_URL =
  typeof __WORKER_URL__ === "string" ? __WORKER_URL__ : "https://api.egocentric.com";

export interface ReleaseInfo {
  version: string;
  build: number;
  key: string;
  sha256: string;
  size: number;
  date: string;
  notes?: string;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("not signed in");
  return { Authorization: `Bearer ${session.access_token}` };
}

export async function latestRelease(): Promise<ReleaseInfo | null> {
  const r = await fetch(`${WORKER_URL}/releases`, { headers: await authHeaders() });
  if (!r.ok) throw new Error(`releases fetch failed: ${r.status}`);
  const doc = await r.json();
  return doc.releases?.[0] ?? null;
}

/** versionCode of the installed package, or null when not installed. */
export async function installedBuild(adb: Adb, pkg: string): Promise<number | null> {
  const out = await adb.subprocess.spawnAndWaitLegacy(
    `dumpsys package ${pkg} 2>/dev/null | grep versionCode || true`);
  const m = String(out).match(/versionCode=(\d+)/);
  return m ? Number(m[1]) : null;
}

export async function downloadApk(
  rel: ReleaseInfo, onProgress?: (done: number, total: number) => void,
): Promise<Uint8Array> {
  const r = await fetch(`${WORKER_URL}/releases/download?key=${encodeURIComponent(rel.key)}`,
    { headers: await authHeaders() });
  if (!r.ok || !r.body) throw new Error(`download failed: ${r.status}`);
  const total = Number(r.headers.get("content-length")) || rel.size;
  const buf = new Uint8Array(total);
  let done = 0;
  const reader = r.body.getReader();
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf.set(value, done);
    done += value.length;
    onProgress?.(done, total);
  }
  if (done !== total) throw new Error(`short download: ${done}/${total} bytes`);

  const digest = await crypto.subtle.digest("SHA-256", buf);
  const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
  if (hex !== rel.sha256) throw new Error(`sha256 mismatch: got ${hex.slice(0, 12)}…, manifest says ${rel.sha256.slice(0, 12)}…`);
  return buf;
}

export async function installApk(
  adb: Adb, apk: Uint8Array, onProgress?: (phase: string) => void,
): Promise<void> {
  const tmp = "/data/local/tmp/capture-update.apk";
  onProgress?.("pushing to device…");
  const sync = await adb.sync();
  try {
    await sync.write({
      filename: tmp,
      file: new Blob([apk]).stream().pipeThrough(new WrapConsumableStream()),
      permission: 0o644,
      mtime: Math.floor(Date.now() / 1000),
    });
  } finally {
    await sync.dispose();
  }
  onProgress?.("installing (pm install)…");
  const out = await adb.subprocess.spawnAndWaitLegacy(`pm install -r ${tmp}; rm -f ${tmp}`);
  if (!/Success/i.test(String(out))) throw new Error(`pm install failed: ${String(out).trim()}`);
}
